import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play } from "lucide-react";
import { Link } from "react-router-dom";
import { SchedulerHealth } from "@/components/admin/SchedulerHealth";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/components/admin/auth";
import { PageTitle, Panel, StatusBadge } from "@/components/admin/AdminLayout";
import { Switch } from "@/components/ui/switch";

const INTERVALS = [[15, "Every 15 min"], [30, "Every 30 min"], [60, "Hourly"], [180, "Every 3 hours"], [360, "Every 6 hours"], [720, "Every 12 hours"], [1440, "Daily"], [10080, "Weekly"]];

export default function Automation() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const { data } = useQuery({ queryKey: ["workflows"], queryFn: () => api.get("/admin/workflows").then((r) => r.data), refetchInterval: 15000 });
  const refresh = () => qc.invalidateQueries({ queryKey: ["workflows"] });
  const update = async (w, patch) => {
    try { await api.put(`/admin/workflows/${w.key}`, { enabled: w.enabled, interval_minutes: w.interval_minutes, ...patch }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async (w) => {
    const t = toast.loading(`Running ${w.name}…`);
    try { const r = await api.post(`/admin/workflows/${w.key}/run`); toast[r.data.status === "success" ? "success" : "error"](r.data.message, { id: t }); refresh(); }
    catch (e) { toast.error(errMsg(e), { id: t }); }
  };
  return (
    <div className="space-y-6" data-testid="admin-automation-page">
      <PageTitle title="Automation" sub="Scheduled jobs run on the server - you don't need to keep this page open. Each job retries twice on failure and raises an alert if it still fails." />
      <SchedulerHealth />
      <div className="grid md:grid-cols-2 gap-4">
        {(data?.workflows || []).map((w) => (
          <div key={w.key} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid={`workflow-card-${w.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div><p className="font-bold">{w.name}</p><p className="text-xs text-slate-500 mt-1">{w.description}</p></div>
              <Switch checked={w.enabled} disabled={!isAdmin} onCheckedChange={(v) => update(w, { enabled: v })} data-testid={`workflow-toggle-${w.key}`} />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {w.daily_setting ? <Link to="/admin/settings" className="h-9 px-3 rounded-md border border-slate-200 flex items-center text-slate-700" data-testid={`workflow-daily-${w.key}`}>Once a day - set the time in Settings</Link> : (
                <select disabled={!isAdmin} value={w.interval_minutes} onChange={(e) => update(w, { interval_minutes: Number(e.target.value) })} className="h-9 rounded-md border border-slate-200 px-2" data-testid={`workflow-interval-${w.key}`}>
                  {INTERVALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
              {isAdmin && <button onClick={() => run(w)} disabled={w.running} data-testid={`workflow-run-${w.key}`} className="h-9 px-3 rounded-md bg-slate-900 text-white font-semibold flex items-center gap-1 disabled:opacity-50"><Play size={14} /> Run now</button>}
            </div>
            <p className="text-xs text-slate-500">Last: {w.last_run_at ? <>{fmtDateTime(w.last_run_at)} <StatusBadge s={w.last_status} /> {w.last_message}</> : "never"}{w.enabled && ` · Next: ${fmtDateTime(w.next_run_at)}`}</p>
          </div>
        ))}
      </div>
      <Panel title="Execution log" testid="workflow-runs-log">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 text-left"><tr><th className="p-2">Started</th><th className="p-2">Job</th><th className="p-2">Trigger</th><th className="p-2">Attempts</th><th className="p-2">Status</th><th className="p-2">Result</th></tr></thead>
          <tbody>{(data?.runs || []).map((r) => (
            <tr key={r.id} className="border-t border-slate-100"><td className="p-2 whitespace-nowrap">{fmtDateTime(r.started_at)}</td><td className="p-2">{r.name}</td><td className="p-2 text-xs">{r.triggered_by}</td><td className="p-2">{r.attempts}</td><td className="p-2"><StatusBadge s={r.status} /></td><td className="p-2 text-xs text-slate-600">{r.message}</td></tr>
          ))}</tbody>
        </table></div>
        {!data?.runs.length && <p className="text-sm text-slate-500">No runs yet.</p>}
      </Panel>
    </div>
  );
}
